import { useEffect, useMemo, useRef, useState } from "react";
import { t, type Locale } from "../i18n";
import { berlinDateKey, formatSlotTime, nextDays } from "../slots";
import type { DropoffAvailability, DropoffSlot } from "../types";

type SlotPickerProps = {
  locale: Locale;
  employeeId?: string | null;
  horizonDays?: number;
  loadSlots: (dateKey: string) => Promise<DropoffAvailability>;
  selectedStartAt?: string | null;
  onSelect: (slot: DropoffSlot) => void;
  onEmployeeId: (id: string) => void;
};

function localDateForLabel(dateKey: string): Date {
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12));
}

export function SlotPicker({
  locale,
  employeeId,
  horizonDays = 14,
  loadSlots,
  selectedStartAt,
  onSelect,
  onEmployeeId,
}: SlotPickerProps) {
  const days = useMemo(() => nextDays(Math.max(1, horizonDays)), [horizonDays]);
  const selectedDateKey = selectedStartAt
    ? berlinDateKey(new Date(selectedStartAt))
    : null;
  const [dateKey, setDateKey] = useState<string | null>(
    selectedDateKey && days.includes(selectedDateKey) ? selectedDateKey : days[0] ?? null
  );
  const [slots, setSlots] = useState<DropoffSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const loadSlotsRef = useRef(loadSlots);
  const onEmployeeIdRef = useRef(onEmployeeId);
  const requestId = useRef(0);

  loadSlotsRef.current = loadSlots;
  onEmployeeIdRef.current = onEmployeeId;

  useEffect(() => {
    if (!dateKey) return;
    const currentRequest = ++requestId.current;
    setLoading(true);
    setFailed(false);
    void loadSlotsRef
      .current(dateKey)
      .then((result) => {
        if (currentRequest !== requestId.current) return;
        setSlots(result.slots);
        onEmployeeIdRef.current(result.employeeId);
      })
      .catch(() => {
        if (currentRequest !== requestId.current) return;
        setSlots([]);
        setFailed(true);
      })
      .finally(() => {
        if (currentRequest === requestId.current) setLoading(false);
      });
  }, [dateKey, employeeId]);

  const availableSlots = slots.filter((slot) => slot.available);

  return (
    <div>
      <h2>{t(locale, "chooseDay")}</h2>
      <div className="day-row">
        {days.map((day) => (
          <button
            key={day}
            type="button"
            className={`chip ${dateKey === day ? "selected" : ""}`}
            onClick={() => setDateKey(day)}
          >
            {new Intl.DateTimeFormat(locale, {
              weekday: "short",
              day: "numeric",
              month: "short",
              timeZone: "Europe/Berlin",
            }).format(localDateForLabel(day))}
          </button>
        ))}
      </div>

      <h2 style={{ marginTop: 20 }}>{t(locale, "chooseSlot")}</h2>
      {loading ? (
        <p className="muted">{t(locale, "loading")}</p>
      ) : availableSlots.length === 0 ? (
        <p className={failed ? "error" : "muted"}>
          {failed ? t(locale, "errorGeneric") : t(locale, "noSlots")}
        </p>
      ) : (
        <div className="slot-row">
          {availableSlots.map((slot) => (
            <button
              key={slot.startAt}
              type="button"
              className={`chip ${selectedStartAt === slot.startAt ? "selected" : ""}`}
              onClick={() => onSelect(slot)}
            >
              {formatSlotTime(new Date(slot.startAt), locale)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
